// Web Audio Solfeggio synthesizer for Meditation mode.
// Generates smooth sine tones with optional binaural offset, harmonics and a shimmer.

export class ChakraAudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.current = null;
    this.droneNode = null;
    this.volume = 0.55;
  }

  init() {
    if (this.ctx) {
      if (this.ctx.state === "suspended") this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
  }

  setVolume(v) {
    this.volume = v;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
    }
  }

  // waveform preset: "solfeggio" (pure), "bowl" (rich harmonics), "binaural"
  play(frequency, { preset = "solfeggio", beat = 6 } = {}) {
    this.init();
    this.stop(0.4);

    const now = this.ctx.currentTime;
    const group = this.ctx.createGain();
    group.gain.setValueAtTime(0, now);
    group.gain.linearRampToValueAtTime(1, now + 1.2); // gentle fade in
    group.connect(this.master);

    const oscs = [];

    const addOsc = (freq, type, gain, pan = 0) => {
      const o = this.ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq;
      const g = this.ctx.createGain();
      g.gain.value = gain;
      // subtle vibrato
      const lfo = this.ctx.createOscillator();
      const lfoGain = this.ctx.createGain();
      lfo.frequency.value = 0.18;
      lfoGain.gain.value = freq * 0.0016;
      lfo.connect(lfoGain).connect(o.frequency);
      lfo.start(now);
      if (pan !== 0 && this.ctx.createStereoPanner) {
        const p = this.ctx.createStereoPanner();
        p.pan.value = pan;
        o.connect(g).connect(p).connect(group);
      } else {
        o.connect(g).connect(group);
      }
      o.start(now);
      oscs.push(o, lfo);
    };

    if (preset === "binaural") {
      addOsc(frequency, "sine", 0.5, -1);
      addOsc(frequency + beat, "sine", 0.5, 1); // binaural beat = `beat` Hz
    } else if (preset === "bowl") {
      addOsc(frequency, "sine", 0.5);
      addOsc(frequency * 2, "sine", 0.14);
      addOsc(frequency * 2.76, "triangle", 0.06);
      addOsc(frequency * 5.4, "sine", 0.03);
    } else {
      addOsc(frequency, "sine", 0.55);
      addOsc(frequency * 2, "sine", 0.08);
    }

    this.current = { group, oscs };
  }

  stop(fade = 0.8) {
    if (!this.current || !this.ctx) return;
    const { group, oscs } = this.current;
    const now = this.ctx.currentTime;
    try {
      group.gain.cancelScheduledValues(now);
      group.gain.setValueAtTime(group.gain.value, now);
      group.gain.linearRampToValueAtTime(0, now + fade);
    } catch (e) {
      /* gain node may already be disconnected; safe to ignore */
      console.debug("audioEngine.stop: gain ramp skipped", e);
    }
    oscs.forEach((o) => {
      try {
        o.stop(now + fade + 0.05);
      } catch (e) {
        /* oscillator may already be stopped; safe to ignore */
        console.debug("audioEngine.stop: oscillator stop skipped", e);
      }
    });
    this.current = null;
  }

  // Soft sustained reference tone (shruti/drone) at `frequency` so a singer can
  // lock onto the pitch by ear. Independent of play()/chime() so it can hold.
  drone(frequency) {
    this.init();
    this.stopDrone(0.15);
    const now = this.ctx.currentTime;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(this.volume * 0.26, now + 0.9);
    g.connect(this.master);

    const o = this.ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = frequency;
    o.connect(g);

    const o2 = this.ctx.createOscillator();
    o2.type = "sine";
    o2.frequency.value = frequency * 2;
    const g2 = this.ctx.createGain();
    g2.gain.value = 0.16;
    o2.connect(g2).connect(g);

    o.start(now);
    o2.start(now);
    this.droneNode = { g, oscs: [o, o2] };
  }

  stopDrone(fade = 0.4) {
    if (!this.droneNode || !this.ctx) return;
    const { g, oscs } = this.droneNode;
    const now = this.ctx.currentTime;
    try {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(0.0001, now + fade);
    } catch (e) {
      console.debug("audioEngine.stopDrone: gain ramp skipped", e);
    }
    oscs.forEach((o) => {
      try {
        o.stop(now + fade + 0.05);
      } catch (e) {
        console.debug("audioEngine.stopDrone: oscillator stop skipped", e);
      }
    });
    this.droneNode = null;
  }

  // short chime used in healing sequence transitions
  chime(frequency, level = 0.6) {
    this.init();
    const now = this.ctx.currentTime;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(this.volume * level, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
    g.connect(this.master);
    [1, 2, 3].forEach((m, i) => {
      const o = this.ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = frequency * m;
      const og = this.ctx.createGain();
      og.gain.value = 0.5 / (i + 1);
      o.connect(og).connect(g);
      o.start(now);
      o.stop(now + 1.7);
    });
  }
}

export const audioEngine = new ChakraAudioEngine();
